/**
 * ============================================================================
 * HomeCriticalSections
 * ============================================================================
 *
 * Purpose: First-fold homepage sections.
 * Hero/stats/programs are static imports so CSS is eligible for experimental
 * inlineCss (<style>) instead of render-blocking <link data-precedence="dynamic">
 * from next/dynamic. Same SSR tree/props — no loading shell, no visual change.
 * Kept as Client Component: converting to SC caused a homepage request reload loop.
 * ============================================================================
 */

"use client";

import { memo, type ReactNode } from "react";
import type {
  HomepageHeroViewModel,
  HomepageProgramsViewModel,
} from "../model/homepage.types";
import { HomePageSection } from "./HomePageSections";
import HeroSectionHomeNew from "@/components/atoms/HeroSectionHomeNew/HeroSectionHomeNew";
import StatsSectionStatic from "@/components/atoms/StatsSectionStaticHomePage/StatsSectionStaticHomePage";
import DegreeProgramWithFilter2 from "@/revamp/sections/DegreeProgramWithFilter2";

interface HomeCriticalSectionsProps {
  hero: HomepageHeroViewModel;
  programs: HomepageProgramsViewModel;
  /** Server Component LCP image — discoverable in initial HTML. */
  lcpMedia?: ReactNode;
}

function HomeCriticalSectionsBase({
  hero,
  programs,
  lcpMedia,
}: HomeCriticalSectionsProps) {
  return (
    <>
      <HomePageSection section="hero">
        <HeroSectionHomeNew
          sliderData={hero.sliderData}
          associatedPrograms={hero.associatedPrograms}
          isMobile={hero.isMobile}
          lcpMedia={lcpMedia}
        />
      </HomePageSection>
      <HomePageSection section="stats">
        <StatsSectionStatic />
      </HomePageSection>
      <HomePageSection section="programs">
        <DegreeProgramWithFilter2
          Programs={programs.programs}
          certificationProgram={programs.certificationProgram}
          country={programs.country}
          schoolOfAIDetails={programs.schoolOfAIPrograms}
        />
      </HomePageSection>
    </>
  );
}

const HomeCriticalSections = memo(HomeCriticalSectionsBase);
HomeCriticalSections.displayName = "HomeCriticalSections";

export default HomeCriticalSections;
